import React, { useCallback, useEffect, useState } from "react";
import { Redirect, Route, Switch } from "react-router-dom";

// import AddIcon from "@mui/icons-material/Add";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import { IconProps, Stack } from "@mui/material";
// import EmptyCardGrowth from "assets/images/EmptyCardGrowth.png";
import { AccessDenied, DefaultViewGuard, HorizontalNavBar, RoleComponentGuard } from "components";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
// import { EmptyCard } from "components/Empty-card/EmptyCard";
import {
  CompareGraph,
  Correlation,
  InvestmentGoalsForm,
  InvestorPicker,
  PieChartsBox,
  SimpleTable
} from "components/Investor-portfolio";
// import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { Routes } from "constants/routes";
import { useActionHook, useNavigate, usePathParam } from "hooks";
import { useAccountSelector, usePickUserPermissions, usePortfolioConstructionActions } from "store";
import { UserPermissionType } from "store/types";

import { ReactComponent as PortfolioIcon } from "../../assets/icons/portfolio-overview.svg";
import { RoleName } from "../../constants";

const iconStyles: IconProps["sx"] = {
  fontSize: "18px"
};

export const InvestorPortfolio: React.FC = () => {
  const {
    getInvestmentGoalsAction,
    getCompareGraphDataAction,
    getPieChartDataAction,
    getPortfolioTableDataAction,
    removeAIconstructionAction
  } = usePortfolioConstructionActions();
  const portfolioConstruction = usePickUserPermissions("portfolio_construction");
  const noAccessPermission = portfolioConstruction && portfolioConstruction === UserPermissionType.NO_ACCESS;
  const navigate = useNavigate();

  // Feature flag for correlation matrix
  const { show_correlations } = useAccountSelector() || {};

  const id = usePathParam("portfolio-construction");

  // const [selectedView, setSelectedView] = useState<"symmetrical" | "custom">("symmetrical");

  const [refresh, setRefresh] = useState(false);

  const { data: goalsData, action: getGoals, setData: setGoals, loading } = useActionHook(getInvestmentGoalsAction);

  const { action: removeAIconstruction } = useActionHook(removeAIconstructionAction, {
    onSuccess: async () => {
      if (!id) return;
      await getGoals(id);
      setRefresh(true);
    }
  });

  const {
    data: allocationData,
    loading: allocationLoading,
    action: getAllocationData,
    setData: setAllocationData
  } = useActionHook(getPortfolioTableDataAction, {
    onSuccess: () => setRefresh(false)
  });

  const {
    data: compareGraphData,
    loading: compareGraphLoading,
    action: getCompareGraphData
  } = useActionHook(getCompareGraphDataAction);

  const {
    action: getPieChartData,
    data: pieChartData,
    loading: pieChartLoading
  } = useActionHook(getPieChartDataAction);

  const getChartsData = useCallback(async () => {
    if (!id) return;
    await getCompareGraphData(id);
    await getPieChartData(id);
  }, [id]);

  useEffect(() => {
    if (!id) return;
    getAllocationData(id, false);
    getChartsData();
    getGoals(id);
  }, [id]);

  if (!id) return null;

  return (
    <RoleComponentGuard
      availableRoles={[RoleName.Admin, RoleName.Analyst]}
      hideChild={noAccessPermission}
      RenderInstead={<AccessDenied redirectClick={() => navigate({ route: "main" })} title="Back home" />}
    >
      <Stack sx={{ width: "100%", overflow: "auto", gap: "16px" }}>
        <InvestorPicker investorId={Number(id)} />
        <HorizontalNavBar
          tabs={[
            {
              icon: <PortfolioIcon style={{ fill: "#0a3142", width: "15px", height: "17px" }} />,
              label: "Allocation",
              value: "allocation",
              matchRouts: [`/main/portfolio-construction/${id}/allocation`],
              onClick: () =>
                navigate({
                  route: "portfolioConstructionInvestorAllocation",
                  params: { investor_id: Number(id) }
                })
            },
            ...(show_correlations
              ? [
                  {
                    icon: <InsertChartOutlinedIcon sx={iconStyles} />,
                    label: "Correlation",
                    value: "correlation",
                    matchRouts: [`/main/portfolio-construction/${id}/correlation`],
                    onClick: () =>
                      navigate({
                        route: "portfolioConstructionInvestorCorrelation",
                        params: { investor_id: Number(id) }
                      })
                  }
                ]
              : [])
          ]}
        />
        <Switch>
          <Redirect
            exact
            from={Routes.portfolioConstructionInvestor}
            to={Routes.portfolioConstructionInvestorAllocation}
          />
          <Route path={Routes.portfolioConstructionInvestorAllocation}>
            <AsyncContentMui isLoading={false}>
              {/* {!allocationData?.length ? (
                <EmptyCard
                  title={EMPTY_CARD_CONTENT.investorPortfolio.title}
                  description={{
                    text: EMPTY_CARD_CONTENT.investorPortfolio.description
                  }}
                  image={{
                    src: EmptyCardGrowth,
                    sx: { width: 315, height: 140 }
                  }}
                  fitPage
                  actionsElement={
                    <Button variant="contained" onClick={() => {}} startIcon={<AddIcon />} sx={{ bgcolor: "#071F35" }}>
                      Add Goals
                    </Button>
                  }
                />
              ) : ( */}
              <>
                <InvestmentGoalsForm
                  investorId={id}
                  setRefresh={() => setRefresh(true)}
                  data={goalsData}
                  setData={setGoals}
                  onRemoveAIConstruction={() => removeAIconstruction(id)}
                  loading={loading}
                />
                <SimpleTable
                  investorId={id}
                  refetch={getChartsData}
                  isNeedToRefresh={refresh}
                  invalidateRefresh={() => setRefresh(false)}
                  data={allocationData ?? []}
                  loading={allocationLoading}
                  getData={getAllocationData}
                  setData={setAllocationData}
                  goalsData={goalsData}
                  setGoalsData={setGoals}
                />
                <CompareGraph loading={compareGraphLoading} data={compareGraphData} />
                <Stack direction="row" gap="20px" justifyContent="space-between" mb="5px">
                  <PieChartsBox loading={pieChartLoading} data={pieChartData} />
                </Stack>
              </>
              {/* )} */}
            </AsyncContentMui>
          </Route>
          <DefaultViewGuard key="portfolio-construction" bookmarkType="correlation_matrix" reportType={null}>
            <Route path={Routes.portfolioConstructionInvestorCorrelation}>
              <Correlation investorId={Number(id)} />
            </Route>
          </DefaultViewGuard>
        </Switch>
      </Stack>
    </RoleComponentGuard>
  );
};
